import { Newspaper } from '@mui/icons-material'
import { Box, Button, Grid } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableOptions,
} from 'material-react-table'
import { FunctionComponent, useMemo, useState } from 'react'

import SimpleContainer from '../../../../base/components/Container/SimpleContainer'
import MontoMonedaTexto from '../../../../base/components/PopoverMonto/MontoMonedaTexto'
import { PAGE_DEFAULT, PageInputProps } from '../../../../interfaces'
import { genApiQuery } from '../../../../utils/helper'
import { MuiTableAdvancedOptionsProps } from '../../../../utils/muiTable/muiTableAdvancedOptionsProps'
import { apiGrupoUnidadMedida } from '../../api/apiGrUniMedListado'
import { GrupoUnidadMedidaListadoProps, GrupoUnidadMedidaProp } from '../../interfaces'
import GrupoUnidadMedidaRegistro from '../registro/GrUniMedRegistroDialog'
import DefinicionCollapse from './DefinicionCollapse'
import GrUniMedListadoMenu from './GrUniMedListadoMenu'

export const tableColumns: MRT_ColumnDef<GrupoUnidadMedidaListadoProps>[] = [
  {
    accessorKey: 'codigoGrupo',
    header: 'Código Grupo',
    size: 120,
    enableSorting: true,
  },
  {
    accessorKey: 'nombreGrupo',
    header: 'Nombre Grupo',
    size: 200,
    enableSorting: true,
  },
  {
    accessorKey: 'unidadMedidaBase',
    header: 'Unidad Medida Base',
    enableColumnFilter: false,
    size: 200,
    enableSorting: false,
    Cell: ({ row }) => {
      const unidad = row.original.unidadMedidaBase
      return (
        <span>
          {unidad?.codigoUnidadMedida ?? '-'} - {unidad?.nombreUnidadMedida ?? '-'}
        </span>
      )
    },
  },
  {
    accessorKey: 'definicion',
    header: 'Nro. Equivalencias',
    size: 200,
    enableSorting: false,
    enableColumnFilter: false,
    enableColumnActions: false,
    Cell: ({ row }) => {
      const definicion = row.original.definicion
      const cantidad = definicion?.length ?? 0

      if (!cantidad) {
        return <span>-</span>
      }

      return (
        <MontoMonedaTexto
          id={`nro-equivalencias-${row.id}`}
          label={undefined}
          monto={cantidad}
          sigla="unidades"
          onChange={() => {}}
        />
      )
    },
  },
]

const GrupoUnidadMedidaListado: FunctionComponent = () => {
  const [openNuevoGrupoUnidadMedida, setOpenNuevoGrupoUnidadMedida] = useState(false)
  // Estados para el datatable principal
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: PAGE_DEFAULT.page,
    pageSize: PAGE_DEFAULT.limit,
  })
  const [rowCount, setRowCount] = useState(0)
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
  const [isReloading, setIsReloading] = useState(false)

  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: [
      'grupoUnidadMedidaListado',
      columnFilters,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
    ],
    queryFn: async () => {
      const query = genApiQuery(columnFilters)
      const fetchPagination: PageInputProps = {
        ...PAGE_DEFAULT,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        reverse: sorting.length <= 0,
        query,
      }
      const { pageInfo, docs } = await apiGrupoUnidadMedida(fetchPagination)
      setRowCount(pageInfo.totalDocs)
      return docs
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
  })

  const handleRefreshTable = async () => {
    setIsReloading(true)
    try {
      await refetch()
    } catch (error) {
      console.error('Error al recargar los datos:', error)
    }
    setIsReloading(false)
  }

  const columns = useMemo(() => tableColumns, [])

  return (
    <SimpleContainer>
      {/* Encabezado: Botón para agregar un nuevo registro */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          onClick={() => setOpenNuevoGrupoUnidadMedida(true)}
          startIcon={<Newspaper />}
          color="primary"
        >
          Nuevo Grupo Unidad Medida
        </Button>
      </Box>
      {/* Renderizado de la tabla principal */}
      <Grid container>
        <Grid size={12}>
          <MaterialReactTable
            {...(MuiTableAdvancedOptionsProps as MRT_TableOptions<GrupoUnidadMedidaListadoProps>)}
            columns={columns}
            data={data ?? []}
            initialState={{
              showColumnFilters: true,
              columnVisibility: { descripcion: false },
              columnOrder: [
                'mrt-row-actions',
                'codigoGrupo',
                'nombreGrupo',
                'unidadMedidaBase',
                'definicion',
              ],
            }}
            enableRowActions
            manualFiltering
            manualPagination
            manualSorting
            enableGlobalFilter={false}
            onColumnFiltersChange={setColumnFilters}
            onPaginationChange={setPagination}
            onSortingChange={setSorting}
            rowCount={rowCount}
            muiToolbarAlertBannerProps={
              isError
                ? { color: 'error', children: 'Error al cargar los datos' }
                : undefined
            }
            state={{
              isLoading,
              columnFilters,
              pagination,
              showAlertBanner: isError,
              showProgressBars: isLoading,
              sorting,
              rowSelection,
              density: 'compact',
            }}
            muiTableBodyCellProps={{
              sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
            positionActionsColumn="first"
            renderRowActions={({ row }) => (
              <GrUniMedListadoMenu id="menu" row={row.original} refetch={refetch} />
            )}
            muiTableProps={{ sx: { tableLayout: 'fixed' } }}
            displayColumnDefOptions={{
              'mrt-row-actions': {
                muiTableHeadCellProps: { align: 'center' },
                size: 120,
              },
            }}
            renderDetailPanel={({ row }) => (
              <Box>
                <DefinicionCollapse
                  definicion={row.original.definicion}
                  unidadMedidaBase={
                    row.original.unidadMedidaBase?.nombreUnidadMedida ?? ''
                  }
                />
              </Box>
            )}
          />
          {/* Diálogo para registrar un nuevo Grupo Unidad Medida */}
          <GrupoUnidadMedidaRegistro
            id="unidadMedidaRegistroDialog"
            keepMounted={false}
            open={openNuevoGrupoUnidadMedida}
            onClose={(value?: GrupoUnidadMedidaProp) => {
              if (value) refetch()
              setOpenNuevoGrupoUnidadMedida(false)
            }}
          />
        </Grid>
      </Grid>
    </SimpleContainer>
  )
}

export default GrupoUnidadMedidaListado
